import React, { useState } from 'react'

import { ChevronDownIcon, DownloadIcon } from '@chakra-ui/icons'
import {
  Button,
  Checkbox,
  Divider,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'

import { axios } from '@/api'

import {
  loadPdfExportPreferences,
  PdfExportPreferences,
  savePdfExportPreferences,
} from './pdf-export-preferences'

interface ExportPdfButtonProps {
  interviewId: string
}

function extractDownloadFilename(
  contentDisposition: string | undefined,
): string | null {
  if (!contentDisposition) {
    return null
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
  const encodedFilename = utf8Match?.[1]

  if (encodedFilename) {
    try {
      return decodeURIComponent(encodedFilename)
    } catch {
      return encodedFilename
    }
  }

  const asciiMatch = contentDisposition.match(
    /filename="([^"]+)"|filename=([^;]+)/i,
  )
  const filename = asciiMatch?.[1] ?? asciiMatch?.[2]

  return filename?.trim() || null
}

export const ExportPdfButton = ({
  interviewId,
}: ExportPdfButtonProps): React.ReactElement => {
  const [isLoading, setIsLoading] = useState(false)
  const [preferences, setPreferences] = useState<PdfExportPreferences>(() =>
    loadPdfExportPreferences(),
  )
  const toast = useToast()
  const disclosure = useDisclosure()

  const updatePreference =
    (key: keyof PdfExportPreferences) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextPreferences = {
        ...preferences,
        [key]: event.target.checked,
      }

      setPreferences(nextPreferences)
      savePdfExportPreferences(nextPreferences)
    }

  const handleExport = async () => {
    setIsLoading(true)

    try {
      const timeZone =
        Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
      const response = await axios.get(
        `/interviews/${interviewId}/export/pdf`,
        {
          responseType: 'blob',
          params: {
            includeScorecard: String(preferences.includeScorecard),
            includeShortTranscript: String(preferences.includeShortTranscript),
            includeTranscript: String(preferences.includeTranscript),
            ...(timeZone ? { timezone: timeZone } : {}),
          },
        },
      )

      const blob = new Blob([response.data as BlobPart], {
        type: 'application/pdf',
      })
      const url = window.URL.createObjectURL(blob)
      const filename =
        extractDownloadFilename(response.headers['content-disposition']) ??
        'interview-report.pdf'

      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = filename
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)

      window.URL.revokeObjectURL(url)
      disclosure.onClose()
    } catch {
      toast({
        title: 'Export failed',
        description: 'Could not generate the PDF report. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Menu closeOnSelect={false} placement={'bottom-end'} isLazy {...disclosure}>
      <MenuButton
        as={Button}
        leftIcon={<DownloadIcon />}
        rightIcon={<ChevronDownIcon />}
        variant={'brand-solid-white'}
        rounded={'1.25rem'}
        whiteSpace={'nowrap'}
        flexShrink={0}
        minW={'fit-content'}
        iconSpacing={1.5}
        aria-label={'Open PDF export options'}
      >
        <Text as={'span'} display={{ base: 'none', md: 'inline' }}>
          PDF
        </Text>
      </MenuButton>

      <MenuList>
        <Flex direction={'column'} px={4} pt={3} pb={2}>
          <Text fontWeight={700}>Include in PDF</Text>
        </Flex>

        <MenuItem
          onClick={() =>
            updatePreference('includeTranscript')({
              target: { checked: !preferences.includeTranscript },
            } as React.ChangeEvent<HTMLInputElement>)
          }
          isDisabled={isLoading}
          fontWeight={700}
        >
          <Checkbox
            isChecked={preferences.includeTranscript}
            pointerEvents={'none'}
            mr={2}
          />
          Raw transcript
        </MenuItem>

        <MenuItem
          onClick={() =>
            updatePreference('includeShortTranscript')({
              target: { checked: !preferences.includeShortTranscript },
            } as React.ChangeEvent<HTMLInputElement>)
          }
          isDisabled={isLoading}
          fontWeight={700}
        >
          <Checkbox
            isChecked={preferences.includeShortTranscript}
            pointerEvents={'none'}
            mr={2}
          />
          Short transcript
        </MenuItem>

        <MenuItem
          onClick={() =>
            updatePreference('includeScorecard')({
              target: { checked: !preferences.includeScorecard },
            } as React.ChangeEvent<HTMLInputElement>)
          }
          isDisabled={isLoading}
          fontWeight={700}
        >
          <Checkbox
            isChecked={preferences.includeScorecard}
            pointerEvents={'none'}
            mr={2}
          />
          Scorecard
        </MenuItem>

        <Divider my={2} mx={'auto'} w={'90%'} borderColor={'gray.300'} />

        <MenuItem
          onClick={handleExport}
          isDisabled={isLoading}
          fontWeight={700}
        >
          <Flex
            w={4}
            mr={2}
            alignItems={'center'}
            justifyContent={'center'}
            flexShrink={0}
          >
            {isLoading ? <Spinner size={'xs'} /> : <DownloadIcon />}
          </Flex>
          {isLoading ? 'Exporting' : 'Download PDF'}
        </MenuItem>
      </MenuList>
    </Menu>
  )
}
